import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@repo/ui/lib/utils';

export interface TextData {
  label?: string;
  primary?: string;
  description?: string;
  caption?: string;
}

const textDisplayVariants = cva('w-full', {
  variants: {
    size: {
      xlg: '',
      lg: '',
      md: '',
      sm: '',
      xsm: '',
    },
    style: {
      left: 'text-left',
      center: 'text-center flex items-center',
    },
    icon: {
      none: '',
      before: 'flex flex-row gap-2 ',
      after: 'flex flex-row gap-2',
      above: 'flex flex-col gap-2',
    },
    color: {
      secondary: [
        'hover:bg-semantic-secondary-background-hover-default',
        '[&_label]:text-semantic-secondary-foreground-strong-default',
        '[&_h2]:text-semantic-secondary-foreground-normal-default',
        '[&_.description]:text-semantic-secondary-foreground-strong-default',
        '[&_.caption]:text-semantic-secondary-foreground-inverse-default',
      ],
      disabled: [
        '[&_label]:text-semantic-disabled-foreground-global-default',
        '[&_h2]:text-semantic-disabled-foreground-global-default',
        '[&_.description]:text-semantic-disabled-foreground-global-default',
        '[&_.caption]:text-semantic-disabled-foreground-global-default',
      ],
    },
  },
  compoundVariants: [
    // Icon size variants
    {
      size: 'xlg',
      icon: ['before', 'after', 'above'],
      className: '[&_svg]:w-8 [&_svg]:h-8',
    },
    {
      size: 'lg',
      icon: ['before', 'after', 'above'],
      className: '[&_svg]:w-6 [&_svg]:h-6',
    },
    {
      size: 'md',
      icon: ['before', 'after', 'above'],
      className: '[&_svg]:w-5 [&_svg]:h-5',
    },
    {
      size: 'sm',
      icon: ['before', 'after', 'above'],
      className: '[&_svg]:w-4 [&_svg]:h-4',
    },
    {
      size: 'xsm',
      icon: ['before', 'after', 'above'],
      className: '[&_svg]:w-3 [&_svg]:h-3',
    },
    // Text size variants
    {
      size: 'xlg',
      className: [
        '[&_label]:typography-semantic-body-1-normal---regular',
        '[&_h2]:typography-semantic-heading-1-bold',
        '[&_.description]:typography-semantic-heading-3---regular',
        '[&_.caption]:typography-semantic-body-2-reading---regular',
      ].join(' '),
    },
    {
      size: 'lg',
      className: [
        '[&_label]:typography-semantic-label-1-normal---regular',
        '[&_h2]:typography-semantic-heading-1-bold',
        '[&_.description]:typography-semantic-body-1-reading---regular',
        '[&_.caption]:typography-semantic-label-1-normal---regular',
      ].join(' '),
    },
    {
      size: 'md',
      className: [
        '[&_label]:typography-semantic-label-2-regular',
        '[&_h2]:typography-semantic-heading-3-bold',
        '[&_.description]:typography-semantic-label-1-reading---regular',
        '[&_.caption]:typography-semantic-label-2-regular',
      ].join(' '),
    },
    {
      size: 'sm',
      className: [
        '[&_label]:typography-semantic-caption-1-regular',
        '[&_h2]:typography-semantic-body-1-normal---bold',
        '[&_.description]:typography-semantic-label-1-normal---regular',
        '[&_.caption]:typography-semantic-caption-1-regular',
      ].join(' '),
    },
    {
      size: 'xsm',
      className: [
        '[&_label]:typography-semantic-caption-1-regular',
        '[&_h2]:typography-semantic-label-1-normal---bold',
        '[&_.description]:typography-semantic-caption-1-regular',
        '[&_.caption]:typography-semantic-caption-2-regular',
      ].join(' '),
    },
  ],
  defaultVariants: {
    size: 'md',
    style: 'left',
    icon: 'none',
    color: 'secondary',
  },
});

interface TextDisplayProps extends VariantProps<typeof textDisplayVariants> {
  textData: TextData;
  className?: string;
  iconClassName?: string;
  children?: React.ReactNode;
}

const TextDisplay = React.forwardRef<HTMLDivElement, TextDisplayProps>(
  (
    {
      className,
      iconClassName,
      size,
      style,
      icon,
      color,
      textData,
      children,
      ...props
    },
    ref
  ) => {
    const IconWrapper = ({
      position,
    }: {
      position: 'before' | 'after' | 'above';
    }) => {
      if (icon !== position) return null;
      return (
        <div className={cn(iconClassName)} role='img' aria-label='icon'>
          {children}
        </div>
      );
    };

    return (
      <div
        className={cn(
          textDisplayVariants({ size, style, icon, color, className })
        )}
        ref={ref}
        {...props}
      >
        <IconWrapper position='above' />
        <IconWrapper position='before' />
        <div className='flex w-full flex-col gap-1 '>
          {textData.label && (
            <label className='label' role='text' aria-label={textData.label}>
              {textData.label}
            </label>
          )}
          {textData.primary && (
            <h2 className='h2' aria-label={textData.primary}>
              {textData.primary}
            </h2>
          )}
          {textData.description && (
            <p
              className='description'
              role='text'
              aria-label={textData.description}
            >
              {textData.description}
            </p>
          )}
          {textData.caption && (
            <p className='caption' role='text' aria-label={textData.caption}>
              {textData.caption}
            </p>
          )}
        </div>
        <IconWrapper position='after' />
      </div>
    );
  }
);

TextDisplay.displayName = 'TextDisplay';

export { TextDisplay, type TextDisplayProps };
